import { Calendar, Clock, ArrowRight, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { BlogPost } from "@/types/blogPost";

interface Category {
  name: string;
  color: string;
  borderColor: string;
  id: string;
  color_hex: string;
  border_color_hex: string;
  text_color_hex: string;
}

interface CategoryGridProps {
  categories: Category[];
  filteredPosts: BlogPost[];
  allPosts: BlogPost[];
  onPostSelect: (post: BlogPost) => void;
  selectedCategory: string | null;
  onCategoryChange: (id: string | null) => void;
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function PostCard({ post, onPostSelect }: { post: BlogPost; onPostSelect: (p: BlogPost) => void }) {
  const category = (post as any).blog_categories as Category | null;
  const slug = (post as any).slug ?? post.id;

  const tagStyle = category?.color_hex
    ? { backgroundColor: category.color_hex + '22', color: category.color_hex, borderColor: category.color_hex + '44' }
    : { backgroundColor: '#1F593822', color: '#1F5938', borderColor: '#1F593844' };

  return (
    <Link
      to={`/blog/${slug}`}
      className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-brand-primary/20 transition-all duration-300 overflow-hidden flex flex-col"
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden shrink-0">
        {post.featured_image_url ? (
          <img
            src={post.featured_image_url}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-brand-primary/8 to-brand-primary/4 flex items-center justify-center">
            <FileText className="w-10 h-10 text-brand-primary/25" />
          </div>
        )}
        {category && (
          <span
            className="absolute top-3 left-3 text-[10px] font-bold font-primary px-2.5 py-1 rounded-full border backdrop-blur-sm"
            style={tagStyle}
          >
            {category.name}
          </span>
        )}
        {post.read_time_minutes && (
          <span className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-black/40 text-white backdrop-blur-sm">
            <Clock className="w-2.5 h-2.5" />
            {post.read_time_minutes} min
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-base font-bold text-gray-800 font-primary leading-snug line-clamp-2 mb-2 group-hover:text-brand-primary transition-colors">
          {post.title}
        </h3>
        {(post.excerpt ?? post.subtitle) && (
          <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 font-primary flex-1 mb-3">
            {post.excerpt ?? post.subtitle}
          </p>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-gray-50 mt-auto">
          <span className="flex items-center gap-1.5 text-gray-400 text-xs font-primary">
            <Calendar className="w-3.5 h-3.5" />
            {post.created_at ? formatDate(post.created_at) : '—'}
          </span>
          <span className="flex items-center gap-1 text-brand-primary font-bold text-xs font-primary">
            Ler mais <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export function CategoryGrid({ categories, filteredPosts, allPosts, onPostSelect, selectedCategory, onCategoryChange }: CategoryGridProps) {
  const sortedCategories = [...categories].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <section className="py-10 sm:py-14">
      <div className="container mx-auto px-4 sm:px-6">

        {/* Category filter chips */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => onCategoryChange(null)}
            className={[
              "px-4 py-2 rounded-full text-sm font-semibold font-primary border transition-all",
              selectedCategory === null
                ? "bg-brand-primary text-white border-brand-primary shadow-sm"
                : "bg-white text-gray-600 border-gray-200 hover:border-brand-primary hover:text-brand-primary",
            ].join(" ")}
          >
            Todos ({allPosts.length})
          </button>
          {sortedCategories.map(cat => {
            const count = allPosts.filter(p => (p as any).blog_categories?.id === cat.id).length;
            const isActive = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => onCategoryChange(isActive ? null : cat.id)}
                className={[
                  "px-4 py-2 rounded-full text-sm font-semibold font-primary border transition-all",
                  isActive
                    ? "bg-brand-primary text-white border-brand-primary shadow-sm"
                    : "bg-white text-gray-600 border-gray-200 hover:border-brand-primary hover:text-brand-primary",
                ].join(" ")}
              >
                {cat.name} ({count})
              </button>
            );
          })}
        </div>

        {/* Post grid */}
        {filteredPosts.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 text-sm font-primary">Nenhum artigo encontrado.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map(post => (
              <PostCard key={post.id} post={post} onPostSelect={onPostSelect} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
